import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constants';
import './IntroVideo.css';

const MAX_VIDEO_DURATION_SECONDS = 180;

/**
 * Intro video upload section with drag-and-drop support.
 *
 * Allows users to upload a video introduction.
 * Includes terms/guidelines notice and 3-minute limit info.
 *
 * @param {Object} props
 * @param {string|null} props.videoUrl - Current video URL
 * @param {function} props.onVideoChange - Handler for video updates
 * @returns {JSX.Element}
 */
function IntroVideo({ videoUrl, onVideoChange, onVideoFile, uploading = false }) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');

  const validateVideoDuration = useCallback((file) => (
    new Promise((resolve, reject) => {
      const previewUrl = URL.createObjectURL(file);
      const probeVideo = document.createElement('video');
      probeVideo.preload = 'metadata';

      const cleanup = () => {
        URL.revokeObjectURL(previewUrl);
        probeVideo.removeAttribute('src');
        probeVideo.load();
      };

      probeVideo.onloadedmetadata = () => {
        const duration = Number(probeVideo.duration);
        cleanup();

        if (!Number.isFinite(duration)) {
          reject(new Error('Could not verify intro video length'));
          return;
        }

        if (duration > MAX_VIDEO_DURATION_SECONDS) {
          reject(new Error('Intro video must be 3 minutes or shorter'));
          return;
        }

        resolve();
      };

      probeVideo.onerror = () => {
        cleanup();
        reject(new Error('Failed to read intro video metadata'));
      };

      probeVideo.src = previewUrl;
    })
  ), []);

  const handleFileSelect = useCallback(
    async (file) => {
      if (file && file.type.startsWith('video/')) {
        try {
          setError('');
          await validateVideoDuration(file);
          const url = URL.createObjectURL(file);
          onVideoChange(url);
          if (onVideoFile) {
            onVideoFile(file, url);
          }
        } catch (validationError) {
          setError(validationError.message || 'Failed to validate intro video');
        }
      }
    },
    [onVideoChange, onVideoFile, validateVideoDuration]
  );

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleRemove = () => {
    setError('');
    onVideoChange(null);
  };

  return (
    <div className="intro-video">
      {/* Header */}
      <div className="intro-video__header">
        <h3 className="intro-video__title">Intro Video</h3>
        {!videoUrl && (
          <label className="intro-video__add-link">
            {uploading ? 'Uploading…' : 'Add Intro Video'}
            <input
              type="file"
              accept="video/*"
              onChange={handleInputChange}
              className="intro-video__hidden-input"
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {/* Video Area */}
      {videoUrl ? (
        <div className="intro-video__preview">
          <video
            src={videoUrl}
            className="intro-video__player"
            controls
            aria-label="Intro video preview"
          />
          <button
            type="button"
            className="intro-video__remove"
            onClick={handleRemove}
            aria-label="Remove video"
            disabled={uploading}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          <div
            className={`intro-video__upload ${isDragging ? 'intro-video__upload--dragging' : ''} ${error ? 'intro-video__upload--error' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <label className="intro-video__upload-content">
              <input
                type="file"
                accept="video/*"
                onChange={handleInputChange}
                className="intro-video__hidden-input"
                disabled={uploading}
              />
              <span className="intro-video__upload-icon" aria-hidden="true">
                {uploading ? '⏳' : '↑'}
              </span>
              <span className="intro-video__upload-prompt">
                {uploading ? 'Uploading your video…' : 'Drag your video here!'}
              </span>
              <span className="intro-video__upload-hint">
                Tip: The max length of an intro video is 3 minutes long!
              </span>
              <span className="intro-video__upload-terms">
                By uploading, you're agreeing to follow our{' '}
                <Link to={ROUTES.TERMS} target="_blank" rel="noopener noreferrer">Terms of Service</Link> and{' '}
                <Link to={ROUTES.GUIDELINES} target="_blank" rel="noopener noreferrer">Community Guidelines</Link>
              </span>
            </label>

            {isDragging && (
              <div className="intro-video__drag-overlay">
                Drop to upload
              </div>
            )}
          </div>
          {error && <div className="intro-video__error">{error}</div>}
        </>
      )}

    </div>
  );
}

IntroVideo.propTypes = {
  videoUrl: PropTypes.string,
  onVideoChange: PropTypes.func.isRequired,
  onVideoFile: PropTypes.func,
  uploading: PropTypes.bool,
};

export default IntroVideo;
